import React from "react";
import {
  BookOpen, FileText, Code, Lightbulb, Zap, Users, CreditCard, CheckCircle2,
} from "lucide-react";

/* ── Doc-specific components for Markdoc tags ──────────────────── */

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  BookOpen, FileText, Code, Lightbulb, Zap, Users, CreditCard, CheckCircle2,
};

export function SectionHeading({ id, title, icon }: { id: string; title: string; icon: string }) {
  const Icon = iconMap[icon] || BookOpen;
  return (
    <div id={id} className="scroll-mt-24 mb-6 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <h2 className="text-2xl font-semibold">{title}</h2>
    </div>
  );
}

export function TocItem({ href, label, icon }: { href: string; label: string; icon: string }) {
  const Icon = iconMap[icon] || BookOpen;
  return (
    <li>
      <a
        href={href}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors py-1"
      >
        <Icon className="h-4 w-4" />
        {label}
      </a>
    </li>
  );
}

export function Step({ num, title, children }: { num: string; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4 p-4 rounded-lg border border-border bg-card">
      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
        <span className="text-sm font-bold text-primary">{num}</span>
      </div>
      <div>
        <h3 className="font-medium mb-1">{title}</h3>
        <div className="text-sm text-muted-foreground">{children}</div>
      </div>
    </div>
  );
}

export function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="p-5 rounded-xl border border-border bg-card">
      <h3 className="font-semibold mb-2">{title}</h3>
      <div className="text-sm text-muted-foreground">{children}</div>
    </div>
  );
}

export function Endpoint({ method, path, description }: { method: string; path: string; description: string }) {
  const color = method === "POST"
    ? "bg-green-500/10 text-green-600 dark:text-green-400"
    : "bg-blue-500/10 text-blue-600 dark:text-blue-400";
  return (
    <div className="flex items-center gap-4 px-5 py-3 border-b border-border last:border-b-0">
      <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded ${color}`}>{method}</span>
      <code className="text-sm font-mono text-foreground">{path}</code>
      <span className="text-sm text-muted-foreground ml-auto hidden md:block">{description}</span>
    </div>
  );
}

export function ScoreRange({ range, label, color, description }: { range: string; label: string; color: string; description: string }) {
  const colorMap: Record<string, string> = {
    green: "text-green-600 dark:text-green-400",
    blue: "text-blue-600 dark:text-blue-400",
    yellow: "text-yellow-600 dark:text-yellow-400",
    red: "text-red-600 dark:text-red-400",
  };
  return (
    <div className="flex items-start gap-4 p-4 rounded-lg border border-border bg-card">
      <div className="text-center shrink-0 w-16">
        <span className={`text-lg font-bold ${colorMap[color] || ""}`}>{range}</span>
      </div>
      <div>
        <h3 className="font-medium mb-1">{label}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}
